import { FormControl, FormGroup } from '@angular/forms';

import { BusinessOperatingSystem } from '@ninety/mobile/platform/api';

import {
  DiscussionCommitmentRating,
  DiscussionModel,
  DiscussionRoleRating,
  DiscussionSeatRating,
  DiscussionType,
  ValueRating,
} from '../discussion.models';

/**
 * Represents a set of possible states for the Discussion form/meeting.
 */
export enum DiscussionFormMode {
  /**
   * Represents a state where the user could not be determined to be a Discussion participant or
   * there is a misconfiguration in the Discussion meeting/form routes.
   *
   * To prevent this state, ensure the logged-in user is a Discussion participant and the form/meeting
   * routes have valid static route Data associated.
   *
   * @see {@link selectDiscussionFormMode}
   */
  None = 'None',
  /** Represents a state where the manager form is displayed. */
  Manager = 'Manager',
  /** Represents a state where the managee form is displayed. */
  Managee = 'Managee',
  /** Represents a state where both the manager and managee forms are displayed. */
  Meeting = 'Meeting',
}

export type DiscussionFormRatingsWithNotes<T> = {
  ratings: T[];
  notes: string;
};

export type DiscussionFormCustomQuestionAnswer = {
  answer: string;
};

export type DiscussionFormSection<T> = {
  manager: T;
  managee: T;
};

export type DiscussionFormSectionKey = keyof Pick<DiscussionFormSection<unknown>, 'manager' | 'managee'>;

export type DiscussionRatingsFormSection<T> = DiscussionFormSection<DiscussionFormRatingsWithNotes<T>>;

export type DiscussionCoreValuesFormValue = DiscussionRatingsFormSection<ValueRating>;
export type DiscussionRoleRatingsFormValue = DiscussionRatingsFormSection<DiscussionRoleRating | DiscussionSeatRating>;
export type DiscussionCommitmentsFormValue = DiscussionRatingsFormSection<DiscussionCommitmentRating>;
export type DiscussionCustomQuestionFormValue = DiscussionFormSection<DiscussionFormCustomQuestionAnswer>[];

export type DiscussionFormValue = {
  coreValues: DiscussionCoreValuesFormValue;
  roleRatings: DiscussionRoleRatingsFormValue;
  leadership: DiscussionCommitmentsFormValue;
  coaching: DiscussionCommitmentsFormValue;
  customQuestions: DiscussionCustomQuestionFormValue;
};

export type DiscussionFormGroup = FormGroup<{
  [P in keyof DiscussionFormValue]: FormControl<DiscussionFormValue[P]>;
}>;

export class Ratings<T> implements DiscussionRatingsFormSection<T> {
  manager = { ratings: [] as T[], notes: '' };
  managee = { ratings: [] as T[], notes: '' };

  constructor(managerRatings: T[], manageeRatings: T[]) {
    this.manager.ratings = managerRatings;
    this.managee.ratings = manageeRatings;
  }

  static forDiscussion(discussion?: DiscussionModel): DiscussionFormValue {
    return {
      coreValues: Ratings.forCoreValues(discussion),
      roleRatings: Ratings.forRoleRatings(discussion),
      leadership: Ratings.forLeadershipCommitments(discussion),
      coaching: Ratings.forCoachingCommitments(discussion),
      customQuestions: Ratings.forCustomQuestions(discussion),
    };
  }

  static forCoreValues(discussion?: DiscussionModel): DiscussionCoreValuesFormValue {
    const formValue = new Ratings<ValueRating>(
      discussion?.managerDiscussionForm?.coreValueRatings?.map(r => r.rating ?? ValueRating.EMPTY) ?? [],
      discussion?.manageeDiscussionForm?.coreValueRatings?.map(r => r.rating ?? ValueRating.EMPTY) ?? []
    );

    formValue.manager.notes = discussion?.managerDiscussionForm?.coreValueNotes ?? '';
    formValue.managee.notes = discussion?.manageeDiscussionForm?.coreValueNotes ?? '';

    return formValue;
  }

  static forRoleRatings(discussion?: DiscussionModel): DiscussionRoleRatingsFormValue {
    const managerRatings =
      discussion?.bos === BusinessOperatingSystem.eos
        ? discussion.managerDiscussionForm?.seatRating
          ? [discussion.managerDiscussionForm.seatRating]
          : []
        : (discussion?.managerDiscussionForm?.roleRatings ?? []);
    const manageeRatings =
      discussion?.bos === BusinessOperatingSystem.eos
        ? discussion.manageeDiscussionForm?.seatRating
          ? [discussion.manageeDiscussionForm.seatRating]
          : []
        : (discussion?.manageeDiscussionForm?.roleRatings ?? []);
    const formValue = new Ratings<DiscussionRoleRating | DiscussionSeatRating>(managerRatings, manageeRatings);

    formValue.manager.notes = discussion?.managerDiscussionForm?.roleNotes ?? '';
    formValue.managee.notes = discussion?.manageeDiscussionForm?.roleNotes ?? '';

    return formValue;
  }

  static forLeadershipCommitments(discussion?: DiscussionModel): DiscussionCommitmentsFormValue {
    const formValue = new Ratings<DiscussionCommitmentRating>(
      discussion?.managerDiscussionForm?.leadershipCommitments ?? [],
      discussion?.manageeDiscussionForm?.leadershipCommitments ?? []
    );

    formValue.manager.notes = discussion?.managerDiscussionForm?.leadershipAssessment?.note ?? '';
    formValue.managee.notes = discussion?.manageeDiscussionForm?.leadershipAssessment?.note ?? '';

    return formValue;
  }

  static forCoachingCommitments(discussion?: DiscussionModel): DiscussionCommitmentsFormValue {
    const formValue = new Ratings<DiscussionCommitmentRating>(
      discussion?.managerDiscussionForm?.coachingCommitments ?? [],
      discussion?.manageeDiscussionForm?.coachingCommitments ?? []
    );

    formValue.manager.notes = discussion?.managerDiscussionForm?.managementAssessment?.note ?? '';
    formValue.managee.notes = discussion?.manageeDiscussionForm?.managementAssessment?.note ?? '';

    return formValue;
  }

  static forCustomQuestions(discussion?: DiscussionModel): DiscussionCustomQuestionFormValue {
    if (!discussion) return [];

    const formValue: DiscussionCustomQuestionFormValue = [];
    const questions =
      discussion?.type === DiscussionType.quarterly ? discussion.quarterlyQuestions : discussion.meetingQuestions;

    questions.forEach(question =>
      formValue.push({
        manager: {
          answer: question.managerAnswer,
        },
        managee: {
          answer: question.manageeAnswer,
        },
      })
    );

    return formValue;
  }
}
